import React, { useEffect, useState, useContext } from 'react';
import { CProgress, CCard, CCardBody, CCardHeader, CCol, CRow } from '@coreui/react';
import { CChart } from '@coreui/react-chartjs';
import { doughnutChartOptions } from '../../../utils/chartTheme';
import axios from 'axios';
import { FirebaseContext } from '../../firebase';
import { AuthUserContext } from '../../session';
import Lottie from "lottie-react";
import dnaAnimationData from '../../../assets/images/Animation1727320560450.json';
import '../../../scss/main.scss';
import config from '../../../utils/EnvironmentSwitcher';

const DnaMetricsDashboard = () => {
  const firebase = useContext(FirebaseContext); // Access Firebase context
  const { authUser } = useContext(AuthUserContext); // Access authenticated user context
  const token = localStorage.getItem('fbAccessToken');
  const [geneticVariants, setGeneticVariants] = useState(null);
  const [sequenceData, setSequenceData] = useState(null);
  const [mutationCount, setMutationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Determine FHIR base URL based on environment
    const fhirBaseUrl = `${config.fhirUrl}/fhir/`;
  // Fetch Genetic Variants (e.g., SNPs, Indels) as FHIR Observation resources
  const fetchGeneticVariants = async () => {
    try {
      const response = await axios.get(
        `${fhirBaseUrl}Observation?subject=${authUser.uid}&code=http://loinc.org|55233-1`,
        {
          headers: {
            'Content-Type': 'application/fhir+json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setGeneticVariants(response.data.entry || []);
    } catch (err) {
      console.error('Error fetching genetic variants:', err);
      setError('Failed to fetch genetic variants.');
    }
  };

  // Fetch Genomic Sequence Data from MolecularSequence FHIR resource
  const fetchSequenceData = async () => {
    try {
      const queryUrl = `${fhirBaseUrl}MolecularSequence?identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}`;

      //Logger.info('Fetching FHIR observations with URL:', queryUrl);

      const response = await axios.get(queryUrl, {
        headers: {
          'Content-Type': 'application/fhir+json',
          Authorization: `Bearer ${token}`,
        },
      });
      setSequenceData(response.data.entry || []);
    } catch (err) {
      console.error('Error fetching sequence data:', err);
      setError('Failed to fetch genomic sequence data.');
    }
  };

  // Fetch the total mutation count (e.g., somatic or germline mutations)
  const fetchMutationCount = async () => {
    try {
      const response = await axios.get(
        `${fhirBaseUrl}Observation?subject=${authUser.uid}&code=http://loinc.org|69548-6`,
        {
          headers: {
            'Content-Type': 'application/fhir+json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const mutations = response.data.entry ? response.data.entry.length : 0;
      setMutationCount(mutations);
    } catch (err) {
      console.error('Error fetching mutation count:', err);
      setError('Failed to fetch mutation count.');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!authUser || !authUser.uid) {
        console.log('No authenticated user found');
        setLoading(false); // Set loading to false when no auth
        return;
      }

      setLoading(true);
      try {
        await fetchGeneticVariants();
        await fetchSequenceData();
        await fetchMutationCount();
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Using sample data instead.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authUser]);

  // Check if we have real data
  const hasRealData =
    (geneticVariants && geneticVariants.length > 0) ||
    (sequenceData && sequenceData.length > 0) ||
    mutationCount > 0;

  // Sample genetic data (replace with real data from your API)
  const geneticMetrics = {
    ancestryComposition: {
      european: 45,
      asian: 30,
      african: 15,
      other: 10
    },
    healthRisks: [
      { condition: 'Type 2 Diabetes', risk: 'Moderate', score: 2.5 },
      { condition: 'Heart Disease', risk: 'Low', score: 1.2 },
      { condition: 'Celiac Disease', risk: 'High', score: 4.0 }
    ],
    traits: {
      eyeColor: 'Brown',
      bloodType: 'O+',
      lactoseIntolerance: 'Likely',
      caffeineSensitivity: 'Normal'
    },
    methylationStatus: {
      global: 75, // percentage
      specificGenes: {
        MTHFR: 'Normal',
        COMT: 'Reduced'
      }
    }
  };

  // Chart data for ancestry composition
  const ancestryChartData = {
    labels: Object.keys(geneticMetrics.ancestryComposition),
    datasets: [
      {
        data: Object.values(geneticMetrics.ancestryComposition),
        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'],
      },
    ],
  };

  const renderAncestryComposition = () => (
    <CCard>
      <CCardHeader>Ancestry Composition</CCardHeader>
      <CCardBody>
        <div className="chart-wrapper" style={{ height: '300px' }}>
          <CChart
            type="doughnut"
            data={ancestryChartData}
            options={doughnutChartOptions}
          />
        </div>
      </CCardBody>
    </CCard>
  );

  const renderHealthRisks = () => (
    <CCard>
      <CCardHeader>Health Risk Assessment</CCardHeader>
      <CCardBody>
        {geneticMetrics.healthRisks.map((risk, index) => (
          <div key={index} className="mb-3 risk-item">
            <div className="d-flex justify-content-between risk-row">
              <span className="risk-label">{risk.condition}</span>
              <span className={`badge risk-badge bg-${getRiskColor(risk.risk)}`}>
                {risk.risk}
              </span>
            </div>
            <CProgress
              value={risk.score * 20}
              color={getRiskColor(risk.risk)}
              className="mt-2"
            />
          </div>
        ))}
      </CCardBody>
    </CCard>
  );

  const renderTraits = () => (
    <CCard>
      <CCardHeader>Genetic Traits</CCardHeader>
      <CCardBody>
        <div className="traits-grid">
          {Object.entries(geneticMetrics.traits).map(([trait, value], index) => (
            <div key={index} className="trait-item">
              <div className="trait-label">{formatTraitLabel(trait)}</div>
              <div className="trait-value">{value}</div>
            </div>
          ))}
        </div>
      </CCardBody>
    </CCard>
  );

  const renderMethylationStatus = () => (
    <CCard>
      <CCardHeader>Methylation Status</CCardHeader>
      <CCardBody>
        <div className="mb-3 methylation-block">
          <h6 className="methylation-title">Global Methylation</h6>
          <CProgress value={geneticMetrics.methylationStatus.global} color="info" />
        </div>
        <div className="specific-genes">
          <h6 className="methylation-title">Gene-Specific Methylation</h6>
          {Object.entries(geneticMetrics.methylationStatus.specificGenes).map(([gene, status], index) => (
            <div key={index} className="d-flex justify-content-between mb-2 methylation-row">
              <span className="methylation-gene">{gene}</span>
              <span className={`badge methylation-badge bg-${getMethylationColor(status)}`}>
                {status}
              </span>
            </div>
          ))}
        </div>
      </CCardBody>
    </CCard>
  );

  // Helper functions
  const getRiskColor = (risk) => {
    switch (risk.toLowerCase()) {
      case 'high': return 'danger';
      case 'moderate': return 'warning';
      case 'low': return 'success';
      default: return 'info';
    }
  };

  const getMethylationColor = (status) => {
    switch (status.toLowerCase()) {
      case 'reduced': return 'warning';
      case 'normal': return 'success';
      case 'elevated': return 'danger';
      default: return 'info';
    }
  };

  const formatTraitLabel = (label) => {
    return label
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .replace(/([A-Z])/g, ' $1')
      .trim();
  };

  // Render genetic variants section
  const renderGeneticVariants = () => (
    <div className="genetic-variants">
      <p>Genetic variant data will be displayed here when available.</p>
      <small className="mb-2">This section shows SNPs, indels, and other genetic variations.</small>
    </div>
  );

  // Render sequence data section
  const renderSequenceData = () => (
    <div className="sequence-data">
      <p>Genomic sequence data will be displayed here when available.</p>
      <small className="mb-2">This section shows raw DNA sequence information.</small>
    </div>
  );

  const renderEducationalContent = () => (
    <CCard className="mb-4">
      <CCardBody>
        <div className="educational-content text-center">
          <h3>Welcome to Your DNA Metrics Dashboard</h3>
          <p>
            DNA, or deoxyribonucleic acid, is the hereditary material in humans and almost all other
            organisms. Nearly every cell in a person's body has the same DNA. Most DNA is located in
            the cell nucleus (nuclear DNA), but a small amount of DNA can also be found in the
            mitochondria (mitochondrial DNA or mtDNA).
          </p>
          <p>
            Understanding your genetic information can provide insights into your health, traits, and
            ancestry. When available, this dashboard will display your genetic variants, genomic
            sequences, and mutation counts.
          </p>
          <p>
            To get started, you can upload your genomic data from a DNA test or connect to a genomic
            service provider.
          </p>

          <div className="dna-animation-container">
            <Lottie
              animationData={dnaAnimationData}
              style={{
                height: 300,
                width: 300,
                margin: '0 auto', // Centers the animation horizontally
                marginTop: '20px'  // Adds some space between text and animation
              }}
            />
          </div>
        </div>
      </CCardBody>
    </CCard>
  );

  return (
    <div className="dna-dashboard">
      <h2>DNA Metrics Dashboard</h2>

      {/* Always show educational content at the top */}
      {renderEducationalContent()}

      {loading ? (
        <div className="text-center">
          <p>Loading DNA Metrics...</p>
        </div>
      ) : (
        <CRow>
          {/* Show sample data if no real data is available */}
          {!hasRealData && (
            <>
              <CCol md={6} className="mb-4">
                {renderAncestryComposition()}
              </CCol>
              <CCol md={6} className="mb-4">
                {renderHealthRisks()}
              </CCol>
              <CCol md={6} className="mb-4">
                {renderTraits()}
              </CCol>
              <CCol md={6} className="mb-4">
                {renderMethylationStatus()}
              </CCol>
            </>
          )}

          {/* Show real data if available */}
          {hasRealData && (
            <CCol xs={12}>
              <CCard className="mb-4">
                <CCardBody>
                  <div className="metric-section">
                    <h3>Genetic Variants (SNPs, Indels)</h3>
                    {renderGeneticVariants()}
                  </div>

                  <div className="metric-section">
                    <h3>Genomic Sequence Data</h3>
                    {renderSequenceData()}
                  </div>

                  <div className="metric-section">
                    <h3>Mutation Count</h3>
                    <p>Total Mutations: {mutationCount}</p>
                    <CProgress value={(mutationCount / 100) * 100} className="mt-2" color="danger" />
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          )}

          {error && (
            <CCol xs={12}>
              <div className="alert alert-info">
                {error} Showing sample data for demonstration.
              </div>
            </CCol>
          )}
        </CRow>
      )}
    </div>
  );
};

export default DnaMetricsDashboard;


